import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Alert,
  HStack,
  Link,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { AlertDismissButton } from '~/components/common/AlertDismissButton'
import { Modal } from '~/components/common/Modal'
import { IntradayFailedBidsTable } from '~/pages/IntradayBidding/components/IntradayFailedBidsTable'
import {
  type IntradaySubmitFailure,
  intradaySubmitFailureReason,
} from '~/pages/IntradayBidding/models/intradaySubmitFailure'

type Props = {
  /** 通った件数 (承認申請・予約も数える)。 0 件の時は見出しを出さず本文だけにする。 */
  passedCount: number
  /** 通らなかった札。 入札制限違反・自動取消時刻の超過で外した札は含めない。 */
  failures: readonly IntradaySubmitFailure[]
  onDismiss: () => void
}

/**
 * 時間前の提出の一部が通らなかった時、 または 1 件も通らず、 画面の更新・作り直しが要る失敗が
 * 1 件でもある時のエラーバナー (AC-BIN-009。 入札全削除の一部失敗〈AC-BIN-138〉と同じ形)。
 * 見出しは通った件数 (0 件の時は出さず本文だけ)、 本文は照合で通らなかった件数 {M} とそれ以外の件数 {K}。
 * 札ごとの理由はバナーに並べず、 「詳細を見る」 のダイアログに出す。
 */
export const IntradaySubmitFailureBanner = ({
  passedCount,
  failures,
  onDismiss,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [detailOpen, setDetailOpen] = useState(false)
  const conflictCount = failures.filter((f) => f.kind === 'conflict').length
  const otherCount = failures.length - conflictCount

  return (
    <>
      <Alert
        flexShrink={0}
        description={
          <>
            {conflictCount > 0 && (
              <Text>
                {t('page:biddingIntraday.submit.failure.conflict', {
                  count: conflictCount,
                })}
              </Text>
            )}
            {otherCount > 0 && (
              <Text>
                {t('page:biddingIntraday.submit.failure.other', {
                  count: otherCount,
                })}
              </Text>
            )}
            <Link marginTop={1} asChild>
              <button onClick={() => setDetailOpen(true)} type="button">
                {t('page:biddingIntraday.bulkResult.showDetails')}
              </button>
            </Link>
          </>
        }
        status="error"
        title={
          passedCount > 0
            ? t('page:biddingIntraday.submit.failure.title', {
                count: passedCount,
              })
            : undefined
        }
      >
        <AlertDismissButton onClick={onDismiss} />
      </Alert>
      <Modal
        footer={
          <HStack justifyContent="flex-end">
            <TertiaryButton onClick={() => setDetailOpen(false)}>
              {t('common:action.close')}
            </TertiaryButton>
          </HStack>
        }
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        size="xl"
        title={t('page:biddingIntraday.submit.failure.dialogTitle')}
      >
        <IntradayFailedBidsTable
          rows={failures.map((f) => ({
            bid: f.bid,
            reason: intradaySubmitFailureReason(f, t),
          }))}
        />
      </Modal>
    </>
  )
}
